/**
 * lib/qrPayload — iter125 shared helpers.
 *
 * Encodes/decodes SplitEasy QR payloads as URLs so native phone cameras
 * (Android Google Lens, iOS camera) treat them as web links and open the
 * PWA directly instead of showing "App not found".
 *
 * URL shape:  {ORIGIN}/s/{urlsafe-base64-of-JSON}
 *
 * Backward-compat: decoder also accepts raw JSON payloads from QR codes
 * generated by iter119–iter123 (already printed / screenshotted in the wild).
 */

const SHARE_PATH = '/s/';

// Pick the canonical app origin. In production we prefer https://split-easy.in
// so screenshotted/printed QRs always work even when the preview URL changes.
function preferredOrigin() {
  if (typeof window === 'undefined') return 'https://split-easy.in';
  const host = window.location.host;
  if (host.endsWith('.preview.emergentagent.com')) {
    // Preview tier — use current origin so dev QRs stay testable.
    return window.location.origin;
  }
  return 'https://split-easy.in';
}

function toUrlSafe(b64) {
  return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function fromUrlSafe(s) {
  const pad = (4 - (s.length % 4)) % 4;
  return s.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat(pad);
}

/** Build a URL that, when scanned by ANY camera, opens the PWA + settle flow. */
export function buildQrUrl(payload) {
  const json = JSON.stringify(payload);
  const b64 = btoa(unescape(encodeURIComponent(json)));
  return preferredOrigin() + SHARE_PATH + toUrlSafe(b64);
}

/** Parse either a URL we generated or a legacy raw-JSON QR. Returns object or null. */
export function parseQrText(text) {
  if (!text) return null;
  const trimmed = text.trim();
  // (1) Try URL form  …/s/<base64>
  try {
    const u = new URL(trimmed);
    const idx = u.pathname.indexOf(SHARE_PATH);
    if (idx >= 0) {
      const tail = u.pathname.slice(idx + SHARE_PATH.length);
      if (tail) {
        const json = decodeURIComponent(escape(atob(fromUrlSafe(tail))));
        const obj = JSON.parse(json);
        if (obj && obj.v === 1) return obj;
      }
    }
  } catch { /* fall-through to raw JSON */ }
  // (2) Try raw JSON (legacy)
  try {
    const obj = JSON.parse(trimmed);
    if (obj && obj.v === 1) return obj;
  } catch { /* not ours */ }
  return null;
}
